import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Check, Rocket, X } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { dunningSelector, selectUserId, selectUserObject } from '@common-reducers/UserSelectors';
import { usePatchUserPreferences } from '@common/hooks/use-patch-user-preferences';
import { useRouter } from '@common-reducers/hooks/use-router';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import {
  backendApi,
  useGetFirstWeekChecklistQuery,
  useRecordFirstWeekEventMutation,
} from '@common-reducers/backend-api/backend-api';
import { selectConnectionKind } from '@common-reducers/ConnectedEnginesSlice';
import { BRAND, brandCtaCss } from '@common/styles/brand-tokens';
import {
  StepId,
  nextStep,
  shouldOfferAlwaysOn,
  withLiveConnection,
  shouldRenderCard,
  stepHref,
} from './first-week-card-logic';
import { useOpenFirstWeekComposer } from './use-open-first-week-composer';
import { GuideVideoButton } from '@/common/components/guide-video/guide-video-button';

const C = {
  ...BRAND,
  borderDone: `color-mix(in srgb, ${BRAND.success} 35%, var(--color-border, #e5e7eb))`,
};

/** Step → the Academy lesson its ▶ plays. */
const STEP_LESSON: Partial<Record<StepId, string>> = { connect: 'whatsapp-web-extension', self_test: 'message-planner' };

// Scoped stylesheet (rendered inline so it reaches the extension's shadow root
// too). Only what inline styles can't express: keyframes + hover/focus states.
const STYLES = `
.bt-fw-row{animation:bt-fw-in .35s cubic-bezier(.2,.8,.2,1) both}
@keyframes bt-fw-in{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:none}}
${brandCtaCss('bt-fw-cta')}
.bt-fw-cta{position:relative;overflow:hidden}
.bt-fw-cta::after{content:"";position:absolute;inset:0;background:linear-gradient(110deg,transparent 30%,rgba(255,255,255,.28) 50%,transparent 70%);transform:translateX(-100%);animation:bt-fw-sheen 4s ease-in-out 1.5s infinite}
@keyframes bt-fw-sheen{0%{transform:translateX(-100%)}30%,100%{transform:translateX(100%)}}
.bt-fw-x{transition:background .15s ease,color .15s ease}
.bt-fw-x:hover{background:${C.track};color:${C.text}!important}
.bt-fw-x:focus-visible{outline:2px solid ${C.accent};outline-offset:2px}
.bt-fw-live{animation:bt-fw-pulse 2s ease-out infinite}
@keyframes bt-fw-pulse{0%{box-shadow:0 0 0 0 color-mix(in srgb,var(--color-accent,#53bdeb) 55%,transparent)}70%,100%{box-shadow:0 0 0 7px transparent}}
.bt-fw-sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
@media (prefers-reduced-motion:reduce){.bt-fw-row,.bt-fw-cta::after,.bt-fw-live{animation:none}}
`;

/** Height of the row; the extension header reserves exactly this (top-header-main.tsx). */
export const FIRST_WEEK_ROW_HEIGHT_EXTENSION = 56;

interface FirstWeekCardProps {
  surface: 'web' | 'extension';
  onVisibilityChange?: (visible: boolean) => void;
  dir?: 'ltr' | 'rtl';
}

/** Horizontal 4-step stepper: numbered dots joined by connectors, each with a
 *  short label — so the four steps explain themselves. */
const Stepper: React.FC<{
  steps: { id: StepId; done: boolean }[];
  current: StepId | null;
  showLabels: boolean;
  label: (id: StepId) => string;
}> = ({ steps, current, showLabels, label }) => (
  <ol style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0, padding: 0, listStyle: 'none', minWidth: 0 }}>
    {steps.map((s, i) => {
      const isCurrent = s.id === current;
      const filled = s.done || isCurrent;
      return (
        <li key={s.id} aria-current={isCurrent ? 'step' : undefined} style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          {i > 0 && (
            <span aria-hidden style={{ width: showLabels ? 24 : 32, height: 2, borderRadius: 2, background: steps[i - 1].done ? C.brand : C.track, flex: '0 0 auto' }} />
          )}
          <span
            aria-hidden
            className={isCurrent ? 'bt-fw-live' : undefined}
            style={{
              width: 22, height: 22, borderRadius: 999, flex: '0 0 auto', boxSizing: 'border-box',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
              background: filled ? C.brand : 'transparent',
              color: filled ? C.brandText : C.muted,
              border: filled ? 'none' : `1.5px solid ${C.trackStrong}`,
            }}
          >
            {s.done ? <Check size={12} strokeWidth={3} /> : i + 1}
          </span>
          {showLabels ? (
            <span style={{ fontSize: 12.5, whiteSpace: 'nowrap', fontWeight: isCurrent ? 600 : 500, color: isCurrent ? C.text : C.muted }}>
              {label(s.id)}
            </span>
          ) : (
            <span className="bt-fw-sr">{label(s.id)}</span>
          )}
        </li>
      );
    })}
  </ol>
);

/**
 * First-week onboarding ROW (#113 FE-1): a full-width 56px row under the top
 * bar, styled like the toolbar rows around it — badge · title + why-this-step
 * hint · labelled 4-step stepper · the current step's CTA · ✕.
 * Step 1 follows the live connection (withLiveConnection); while waiting for the
 * real send, a user without a live gateway is offered the always-on connection.
 * Green "Setup complete" shows briefly, then the row collapses and never returns.
 *
 * The 4 steps, the get-first-week-checklist contract, the render-gate and the
 * emitted events are unchanged.
 */
const FirstWeekCard: React.FC<FirstWeekCardProps> = ({ surface, onVisibilityChange, dir }) => {
  const { t } = useTranslation(namespace);
  const { navigate } = useRouter();
  const dispatch = useAppDispatch();
  const openComposer = useOpenFirstWeekComposer(surface);
  const userId = useAppSelector(selectUserId);
  const { isDunning } = useAppSelector(dunningSelector);

  // The row's state is per ACCOUNT, on the user document (preferences.firstWeek) — closing or
  // finishing it on one account never hides it for another, and it follows the account across
  // devices. The local values only make a click take effect before the save comes back.
  const firstWeek = useAppSelector(selectUserObject)?.preferences?.firstWeek;
  const patchPreferences = usePatchUserPreferences();
  const markFirstWeek = (key: 'dismissedAt' | 'completedAt' | 'seenAt') => {
    patchPreferences((p) => ({ firstWeek: { ...p.firstWeek, [key]: new Date().toISOString() } })).catch(() => { /* retried next time it applies */ });
  };
  const [dismissedNow, setDismissed] = useState(false);
  const [acknowledgedNow, setAcknowledged] = useState(false);
  const [seenNow, setSeenWorking] = useState(false);
  // Another account signed in: nothing local carries over.
  useEffect(() => { setDismissed(false); setAcknowledged(false); setSeenWorking(false); }, [userId]);
  const dismissed = dismissedNow || !!firstWeek?.dismissedAt;
  const acknowledged = acknowledgedNow || !!firstWeek?.completedAt;
  const seenWorking = seenNow || !!firstWeek?.seenAt;
  // Once dismissed or completed the row never renders again — stop asking.
  const { data: serverPayload } = useGetFirstWeekChecklistQuery(undefined, { skip: !userId || dismissed || acknowledged });
  const kind = useAppSelector(selectConnectionKind);
  // Step 1 follows the LIVE connection (see withLiveConnection); the rest is the server's record.
  const payload = useMemo(
    () => serverPayload && { ...serverPayload, steps: withLiveConnection(serverPayload.steps, kind) },
    [serverPayload, kind],
  );
  const [recordFirstWeekEvent] = useRecordFirstWeekEventMutation();

  const [entered, setEntered] = useState(false);     // mount: height 0 → row height
  const [collapsing, setCollapsing] = useState(false); // unmount: back to 0
  const [rowWidth, setRowWidth] = useState<number>(1200);
  const shownRef = useRef(false);
  const ackTimerRef = useRef<number | undefined>(undefined);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const working = shouldRenderCard(payload, !!isDunning, dismissed);
  const gatePasses = !!payload && payload.steps.length === 4 && !isDunning && !dismissed;
  const allDone = !!payload && payload.steps.length === 4 && payload.steps.every((s) => s.done);
  const showComplete = gatePasses && allDone && !acknowledged && seenWorking;
  const rendered = (working || showComplete) && !collapsing;
  const currentStep: StepId | null = payload ? nextStep(payload.steps) : null;

  // Mount collapse-in.
  useEffect(() => {
    if (rendered) {
      const id = window.requestAnimationFrame(() => setEntered(true));
      return () => window.cancelAnimationFrame(id);
    }
    setEntered(false);
    return undefined;
  }, [rendered]);

  // Report visibility (the extension header reserves the row's height while it shows).
  useEffect(() => {
    onVisibilityChange?.(rendered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rendered]);

  // Remember that this browser has shown the working row (gates "Setup complete").
  useEffect(() => {
    if (!working || seenWorking) return;
    markFirstWeek('seenAt');
    setSeenWorking(true);
  }, [working, seenWorking]); // eslint-disable-line react-hooks/exhaustive-deps

  // "shown" event once.
  useEffect(() => {
    if (!working || !currentStep || shownRef.current) return;
    shownRef.current = true;
    recordFirstWeekEvent({ name: 'first_week_checklist_shown', props: { step: currentStep, surface } }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [working, currentStep]);

  // On completion: show the green row briefly, then collapse + acknowledge (never render again).
  useEffect(() => {
    if (!showComplete) return undefined;
    ackTimerRef.current = window.setTimeout(() => {
      setCollapsing(true);
      window.setTimeout(() => {
        markFirstWeek('completedAt');
        setAcknowledged(true);
      }, 220);
    }, 5000);
    return () => window.clearTimeout(ackTimerRef.current);
  }, [showComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch on focus while the row is live (a step may complete elsewhere).
  useEffect(() => {
    if (!working) return undefined;
    const onFocus = () => {
      dispatch(backendApi.endpoints.getFirstWeekChecklist.initiate(undefined, { forceRefetch: true }));
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [dispatch, working]);

  // Responsive: collapse elements by the row's own width.
  useLayoutEffect(() => {
    if (!rootRef.current || typeof ResizeObserver === 'undefined') return undefined;
    const el = rootRef.current;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setRowWidth(e.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [rendered]);

  if (!rendered || !payload) return null;

  const total = payload.steps.length;
  const doneCount = payload.steps.filter((s) => s.done).length;

  const fireClicked = (step: StepId) => {
    try {
      recordFirstWeekEvent({ name: 'first_week_checklist_step_clicked', props: { step, surface } }).catch(() => {});
    } catch { /* never break the row over analytics */ }
  };

  const handleDismiss = () => {
    markFirstWeek('dismissedAt');
    setCollapsing(true);
    window.setTimeout(() => setDismissed(true), 220);
  };

  const handleCtaClick = (step: StepId) => {
    fireClicked(step);
    try {
      // 'connect' leads to the focused onboarding; the always-on offer on 'return' to the connections page.
      if (step === 'connect' || step === 'return') {
        const href = stepHref(step, surface);
        if (!href) return;
        if (surface === 'extension') window.open(href, '_blank');
        else navigate(href);
        return;
      }
      openComposer(step);
    } catch { /* never break the row on a CTA failure */ }
  };

  const stepLabel = (id: StepId) => t(`firstWeekCard.short.${id}`);
  const offerAlwaysOn = shouldOfferAlwaysOn(currentStep, kind);
  // While waiting for the real send, say plainly what it depends on.
  const hintKey = currentStep === 'return'
    ? `firstWeekCard.hint.${offerAlwaysOn ? 'return_local' : 'return_remote'}`
    : `firstWeekCard.hint.${currentStep}`;
  const ctaLabel = offerAlwaysOn ? t('firstWeekCard.rowCta.always_on') : t(`firstWeekCard.rowCta.${currentStep}`);
  const currentIndex = Math.min(doneCount + 1, total);
  const stepOf = t('firstWeekCard.stepOf', { current: currentIndex, total });
  const showStepper = rowWidth >= 820;
  const showStepLabels = rowWidth >= 1180;
  const showHint = rowWidth >= 560;
  const ROW_H = FIRST_WEEK_ROW_HEIGHT_EXTENSION;

  return (
    <div
      ref={rootRef}
      dir={dir}
      style={{
        overflow: 'hidden',
        height: entered ? ROW_H : 0,
        opacity: entered ? 1 : 0,
        transition: 'height .28s cubic-bezier(.2,.8,.2,1), opacity .2s ease',
      }}
    >
      <style>{STYLES}</style>
      <div
        className="bt-fw-row"
        style={{
          boxSizing: 'border-box',
          height: ROW_H,
          display: 'flex',
          alignItems: 'center',
          gap: 24,
          paddingInline: 16,
          background: allDone ? C.wash(C.success) : C.wash(C.accent, 9),
          borderBottom: `1px solid ${allDone ? C.borderDone : C.border}`,
          transition: 'background .3s ease',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: '0 1 auto' }}>
          <span
            aria-hidden
            style={{
              width: 32, height: 32, borderRadius: 10, flex: '0 0 auto',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: allDone ? C.success : C.brand, color: allDone ? '#fff' : C.brandText,
              boxShadow: C.brandShadow,
            }}
          >
            {allDone ? <Check size={17} strokeWidth={3} /> : <Rocket size={16} strokeWidth={2.25} />}
          </span>
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.35 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {allDone ? t('firstWeekCard.setupComplete') : t('firstWeekCard.title')}
              </span>
              {/* Progress for assistive tech; a visible chip only when the stepper is hidden. */}
              {!allDone && (
                <span
                  role="progressbar" aria-label={t('firstWeekCard.title')} aria-valuenow={doneCount} aria-valuemin={0} aria-valuemax={total} aria-valuetext={stepOf}
                  className={showStepper ? 'bt-fw-sr' : undefined}
                  style={showStepper ? undefined : { flex: '0 0 auto', fontSize: 11, fontWeight: 600, color: C.muted, background: C.track, borderRadius: 999, padding: '1px 8px', whiteSpace: 'nowrap' }}
                >
                  {stepOf}
                </span>
              )}
            </span>
            {showHint && !allDone && currentStep && (
              <span aria-live="polite" style={{ fontSize: 12.5, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t(hintKey)}
              </span>
            )}
          </span>
        </span>

        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
          {showStepper && !allDone && (
            <Stepper steps={payload.steps} current={currentStep} showLabels={showStepLabels} label={stepLabel} />
          )}
        </div>

        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flex: '0 0 auto' }}>
          {/* ▶ for the current step's lesson (connect → 1.3, self-test → 2.1); nothing without a video. */}
          {!allDone && currentStep && STEP_LESSON[currentStep] && (
            <GuideVideoButton key={currentStep} lesson={STEP_LESSON[currentStep] ?? ''} variant="icon" surface={`first_week_${currentStep}`} />
          )}
          {!allDone && currentStep && (currentStep !== 'return' || offerAlwaysOn) && (
            <button
              type="button"
              className="bt-fw-cta"
              onClick={() => handleCtaClick(currentStep)}
              style={{
                background: C.brand, color: C.brandText, border: 'none',
                fontSize: 13, fontWeight: 600, height: 34, padding: '0 16px', borderRadius: 9,
                whiteSpace: 'nowrap', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
                boxShadow: C.brandShadow, fontFamily: 'inherit',
              }}
            >
              {ctaLabel}
              <ArrowRight className="bt-arrow" size={15} strokeWidth={2.5} />
            </button>
          )}
          <button
            type="button"
            className="bt-fw-x"
            onClick={handleDismiss}
            aria-label={allDone ? t('firstWeekCard.dismissComplete') : t('firstWeekCard.dismiss')}
            style={{
              width: 32, height: 32, borderRadius: 8, border: 'none', background: 'transparent',
              color: C.muted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
            }}
          >
            <X size={17} strokeWidth={2.25} />
          </button>
        </span>
      </div>
    </div>
  );
};

export default FirstWeekCard;
